import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type ButtonHTMLAttributes } from "react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "mada-btn inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      // Hiérarchie d'action : `default` (teal plein) = action PRIMAIRE unique par
      // écran ; `outline`/`ghost` = secondaires, retour au survol en teinte teal
      // (cohérent avec le hover des tables) au lieu d'atténuer le texte ;
      // `secondary` = variante tonale teal ; `destructive` = rouge. Palette et
      // tokens inchangés — on ne fait que renforcer l'affordance et la lisibilité.
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/80",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90 active:bg-destructive/80",
        outline:
          "border border-border bg-surface text-foreground hover:border-primary/40 hover:bg-primary/5 active:bg-primary/10",
        secondary: "bg-primary/10 text-primary hover:bg-primary/15 active:bg-primary/20",
        ghost: "text-muted-foreground hover:bg-primary/5 hover:text-foreground active:bg-primary/10",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded px-3 text-xs",
        lg: "h-11 rounded px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    // `data-variant` expose la hiérarchie d'action au CSS : cibler un variant
    // par ses classes utilitaires est piégeux (`hover:bg-primary/5` du variant
    // `outline` ressemble au fond plein de `default`).
    return (
      <Comp
        data-variant={variant ?? "default"}
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = "Button";

export { buttonVariants };
